import { Button } from "antd";
import React, { useEffect } from "react";
import { BsExclamationTriangle } from "react-icons/bs";
import { AdFetchingErrorFallback } from "../../../components/shared/ErrorBoundary";
import AdsSkeleton from "./AdsSkeleton";
import AdsControls from "./AdsControls";
import AdsSection from "./AdsSection";
import EmptyAdsState from "./EmptyAdsState";
import { useHomeAds } from "../hooks/useHomeAds";
import './homStyle.css';
import Pagination from "../../../components/shared/Pagination";

const HomeAds: React.FC = () => {
    const {
        // State
        premiumAds,
        verifiedAds,
        isLoading,
        sort,
        displayAds,
        favorites,
        isTogglingFavorite,
        premiumAdsFetchFailed,
        verifiedAdsFetchFailed,
        activeTab,
        currentPage,
        regularAds,
        regularAdsLoading,
        fetchFailed,
        paginatedRegularAds,
        totalAllAds,
        PAGE_SIZE,

        // Actions
        setSort,
        setDisplayAds,
        setActiveTab,
        toggleFavorite,
        handlePageChange,
        retryFetch,
        retryVerifiedAdsFetch
    } = useHomeAds();

    useEffect(() => {
        // Restore scroll position if it exists
        const savedPosition = sessionStorage.getItem('adsScrollPosition');
        if (savedPosition) {
            window.scrollTo(0, parseInt(savedPosition));
            // Clear the saved position after restoring
            sessionStorage.removeItem('adsScrollPosition');
        }
    }, []);

    const totalPagesAll = Math.ceil(totalAllAds / PAGE_SIZE);

    return (
        <div className="max-w-full mx-auto">
            <AdsControls
                activeTab={activeTab}
                displayAds={displayAds}
                sort={sort}
                onTabChange={setActiveTab}
                onDisplayChange={setDisplayAds}
                onSortChange={setSort}
            />

            {isLoading || regularAdsLoading ? (
                <AdsSkeleton displayType={displayAds} count={8} />
            ) : (
                <div>
                    {activeTab === 'all' && (
                        <>
                            <AdsSection
                                title="Toutes les annonces"
                                ads={paginatedRegularAds}
                                displayType={displayAds}
                                favorites={favorites}
                                isTogglingFavorite={isTogglingFavorite}
                                onToggleFavorite={toggleFavorite}
                            />
                        </>
                    )}

                    {activeTab === 'premium' && (
                        <AdsSection
                            title="Articles Premium"
                            ads={premiumAds}
                            displayType={displayAds}
                            isPremium={true}
                            favorites={favorites}
                            isTogglingFavorite={isTogglingFavorite}
                            onToggleFavorite={toggleFavorite}
                        />
                    )}

                    {activeTab === 'verified' && (
                        verifiedAdsFetchFailed ? (
                            <div className="mb-8">
                                <div className="flex items-center gap-2 mb-4">
                                    <h2 className="text-lg font-bold text-gray-800 flex items-center">
                                        Annonces Vérifiées
                                    </h2>
                                </div>
                                <AdFetchingErrorFallback
                                    retryFetch={retryVerifiedAdsFetch}
                                    isLoading={isLoading}
                                />
                            </div>
                        ) : (
                            <AdsSection
                                title="Annonces Vérifiées"
                                ads={verifiedAds}
                                displayType={displayAds}
                                isVerified={true}
                                favorites={favorites}
                                isTogglingFavorite={isTogglingFavorite}
                                onToggleFavorite={toggleFavorite}
                            />
                        )
                    )}

                    {activeTab === 'all' && totalAllAds > PAGE_SIZE && (
                        <div className="flex justify-center py-8 px-4">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAll}
                                onPageChange={handlePageChange}
                                totalItems={totalAllAds}
                                pageSize={PAGE_SIZE}
                            />
                        </div>
                    )}

                    {fetchFailed &&
                        // ? 
                        (
                            <div className="bg-white p-8 rounded-lg shadow-sm flex flex-col items-center">
                                <BsExclamationTriangle className="text-amber-500 text-4xl mb-4" />
                                <h2 className="text-xl font-semibold mb-2">Annonces non disponibles</h2>
                                <p className="text-gray-600 text-center mb-4">
                                    Nous n'avons pas pu charger les annonces. Veuillez réessayer.
                                </p>
                                <Button
                                    onClick={retryFetch}
                                    loading={regularAdsLoading}
                                    type="primary"
                                >
                                    Réessayer
                                </Button>
                            </div>
                        )
                        // : (
                        //     <>
                        //         {activeTab === 'all' && regularAds.length > 0 && (
                        //             <div className="text-center text-gray-500 my-4">
                        //                 Page {currentPage} sur {Math.ceil(regularAds.length / PAGE_SIZE)}
                        //             </div>
                        //         )}
                        //     </>
                        // )
                    }
                </div>
            )}

            {!isLoading && !regularAdsLoading && regularAds.length === 0 && premiumAds.length === 0 && verifiedAds.length === 0 &&
                !fetchFailed && !premiumAdsFetchFailed && !verifiedAdsFetchFailed && (
                    <EmptyAdsState />
                )}
        </div>
    );
};

export default HomeAds;
